import {
  getSocket,
  addSocketOpenListener,
  addSocketReplacedListener,
  addSessionEndedListener,
} from './channelService';

/**
 * WS transport for a single ACTIVE chat conversation. To minimize network and
 * memory we join only the conversation the user is looking at (global unread
 * for the rest arrives via the user:{id} channel — see channelService). When
 * the user switches conversations we leave the old topic and join the new one.
 *
 * Callbacks: { onJoined, onMessage, onMessageUpdated, onMessageDeleted,
 * onMessageReacted, onTyping, onPresenceState, onPresenceDiff, onRejoin }.
 * onRejoin fires on a post-reconnect second join so the caller can gap-fill
 * missed messages. (Read receipts do NOT arrive on this topic — they fan out
 * on the always-joined user:{id} channel, see channelService.)
 */
let chatChannel = null;
let joinedId = null;
let desiredId = null;
let pendingUnsub = null;
let socketReplacedUnsub = null;
let callbacks = {};

function clearPending() {
  if (pendingUnsub) {
    pendingUnsub();
    pendingUnsub = null;
  }
}

// COMMS-04: a region failover replaces channelService's socket outright, but
// `chatChannel` still points at a Channel bound to the old, now-dead one —
// the "already joined" guard below would treat that stale reference as
// success forever, so a user sitting in an open conversation through a
// failover would see no live messages/typing/reactions for the rest of the
// session. On this signal, drop the stale reference (no leave() — there is
// nothing listening on the dead transport) and rejoin the active
// conversation for real on the new socket.
function ensureSocketReplacedRetry() {
  if (socketReplacedUnsub) return;
  socketReplacedUnsub = addSocketReplacedListener(() => {
    if (!desiredId) return;
    chatChannel = null;
    joinedId = null;
    joinChatChannel(desiredId, callbacks);
  });
}

// A session end clears channelService's socketReplacedHandlers Set, but
// ensureSocketReplacedRetry() above only ever registers into it ONCE per
// module lifetime (guarded by socketReplacedUnsub). Without resetting that
// guard too, it stays non-null forever after the first session end, so no
// later login this app session ever re-subscribes — silently reintroducing
// COMMS-04 (a later failover swaps in a socket nobody told this service
// about) starting from the second login.
//
// Guard-only was not enough on its own, though (see addSessionEndedListener's
// invariant doc): `chatChannel`/`joinedId`/`desiredId`/`callbacks` are this
// module's PRIMARY state, and until this fix they were reset only by
// leaveChatChannel() being called from useChatRealtime.js's own effect
// cleanup (keyed on user?.id/isAuthenticated) — a mounted-component
// dependency, not a guarantee. A session end that isn't immediately followed
// by that effect re-running (or by no chat surface being mounted at all)
// left the departed account's joined chat channel live for whoever signs in
// next on this device. leaveChatChannel() resets everything this module
// mirrors, so calling it here — in addition to nulling the guard — is what
// makes this module actually reset ITSELF, not just its retry plumbing.
//
// Registered once, unconditionally, at module load — this subscription
// itself must survive every teardown, so it does not go through the guarded
// ensureSocketReplacedRetry().
addSessionEndedListener(() => {
  socketReplacedUnsub = null;
  leaveChatChannel();
});

export function joinChatChannel(channelId, cbs = {}) {
  if (!channelId) {
    leaveChatChannel();
    return Promise.resolve();
  }
  ensureSocketReplacedRetry();
  if (joinedId === channelId && chatChannel) {
    callbacks = cbs;
    return Promise.resolve();
  }

  leaveChatChannel();
  desiredId = channelId;
  callbacks = cbs;

  const socket = getSocket();
  if (!socket) {
    pendingUnsub = addSocketOpenListener(() => {
      clearPending();
      if (desiredId) joinChatChannel(desiredId, callbacks);
    });
    return Promise.resolve();
  }

  const channel = socket.channel(`chat:${channelId}`, {});
  chatChannel = channel;
  joinedId = channelId;

  channel.on('message', (p) => callbacks.onMessage?.(p));
  channel.on('message_updated', (p) => callbacks.onMessageUpdated?.(p));
  channel.on('message_deleted', (p) => callbacks.onMessageDeleted?.(p));
  channel.on('message_reacted', (p) => callbacks.onMessageReacted?.(p));
  channel.on('typing', (p) => callbacks.onTyping?.(p));
  channel.on('presence_state', (p) => callbacks.onPresenceState?.(p));
  channel.on('presence_diff', (p) => callbacks.onPresenceDiff?.(p));

  return new Promise((resolve) => {
    let okCount = 0;
    channel
      .join()
      .receive('ok', (resp) => {
        okCount += 1;
        // Join reply carries { channel_key, server_seq } — the member-gated
        // delivery path for the attachment-encryption key.
        callbacks.onJoined?.(resp || {});
        if (okCount > 1) callbacks.onRejoin?.({ channelId });
        resolve();
      })
      .receive('error', (err) => {
        console.warn('Chat channel join error:', err);
        resolve();
      })
      .receive('timeout', () => resolve());
  });
}

export function leaveChatChannel() {
  clearPending();
  desiredId = null;
  if (chatChannel) chatChannel.leave();
  chatChannel = null;
  joinedId = null;
  callbacks = {};
}

export function getJoinedChatId() {
  return joinedId;
}

/** Send a message; resolves with { uid, id, server_seq } from the server. */
export function sendChatMessage(channelId, payload) {
  if (!chatChannel || joinedId !== channelId) return Promise.reject(new Error('not joined'));
  return new Promise((resolve, reject) => {
    chatChannel
      .push('message', payload)
      .receive('ok', resolve)
      .receive('error', reject)
      .receive('timeout', () => reject(new Error('timeout')));
  });
}

/** Edit a sent message's body (and, for a `code` message, its highlighted
 *  `language`). Resolves on server ack; the edited copy arrives via the
 *  `message_updated` broadcast. */
export function editChatMessage(channelId, { id, body, language }) {
  if (!chatChannel || joinedId !== channelId) return Promise.reject(new Error('not joined'));
  return new Promise((resolve, reject) => {
    chatChannel
      .push('edit', language ? { id, body, language } : { id, body })
      .receive('ok', resolve)
      .receive('error', reject)
      .receive('timeout', () => reject(new Error('timeout')));
  });
}

/** Unsend (soft-delete) a message. The removal arrives via `message_deleted`. */
export function deleteChatMessage(channelId, { id }) {
  if (!chatChannel || joinedId !== channelId) return Promise.reject(new Error('not joined'));
  return new Promise((resolve, reject) => {
    chatChannel
      .push('delete', { id })
      .receive('ok', resolve)
      .receive('error', reject)
      .receive('timeout', () => reject(new Error('timeout')));
  });
}

/** Toggle (add/remove) an emoji reaction on a message. The result arrives via `message_reacted`. */
export function toggleChatReaction(channelId, { id, emoji }) {
  if (!chatChannel || joinedId !== channelId) return Promise.reject(new Error('not joined'));
  return new Promise((resolve, reject) => {
    chatChannel
      .push('react', { id, emoji })
      .receive('ok', resolve)
      .receive('error', reject)
      .receive('timeout', () => reject(new Error('timeout')));
  });
}

export function sendTyping(channelId) {
  if (chatChannel && joinedId === channelId) chatChannel.push('typing', {});
}

/**
 * Advance the read cursor over the live topic. Returns the Phoenix Push (so
 * the caller can hook error/timeout and retry later), or null when this
 * conversation isn't the joined one.
 */
export function sendRead(channelId, seq) {
  if (chatChannel && joinedId === channelId) return chatChannel.push('read', { seq });
  return null;
}

/** Ask the server for the channel's current server_seq (reconnect gap check). */
export function pingChatSeq(channelId) {
  if (!chatChannel || joinedId !== channelId) return Promise.resolve(null);
  return new Promise((resolve) => {
    chatChannel
      .push('sync:ping', {})
      .receive('ok', (r) => resolve(r.server_seq))
      .receive('error', () => resolve(null))
      .receive('timeout', () => resolve(null));
  });
}
